import { ChevronDown } from '@carbon/icons-react';
import type { ReactNode } from 'react';
import { useId } from 'react';
import type { IconComponent, LinkComponent } from '../../types';
import { cx } from '../../utils';
import styles from './CdNavItem.module.scss';

const DefaultLink: LinkComponent = ({ href, children, ...rest }) => (
  <a href={href} {...rest}>
    {children}
  </a>
);

export interface CdNavItemProps {
  label: string;
  href: string;
  icon?: IconComponent;
  active?: boolean;
  /** `rail` shows the icon only (label becomes the accessible name / tooltip). */
  mode?: 'expanded' | 'rail';
  /** Indentation level inside groups (0 = top level). */
  depth?: 0 | 1 | 2;
  linkComponent?: LinkComponent;
  onNavigate?: () => void;
}

/** A single navigation link for the navy side nav. */
export const CdNavItem = ({
  label,
  href,
  icon: Icon,
  active = false,
  mode = 'expanded',
  depth = 0,
  linkComponent: LinkEl = DefaultLink,
  onNavigate,
}: CdNavItemProps) => {
  const isRail = mode === 'rail';
  return (
    <LinkEl
      href={href}
      className={cx(
        styles.item,
        styles[`depth${depth}`],
        active && styles.active,
        isRail && styles.rail,
      )}
      aria-current={active ? 'page' : undefined}
      title={isRail ? label : undefined}
      onClick={onNavigate}
    >
      {Icon && <Icon className={styles.icon} aria-hidden="true" />}
      {isRail ? (
        <span className={styles.srOnly}>{label}</span>
      ) : (
        <span className={styles.label}>{label}</span>
      )}
    </LinkEl>
  );
};

export interface CdNavGroupProps {
  label: string;
  icon?: IconComponent;
  depth?: 0 | 1;
  expanded: boolean;
  onToggle: () => void;
  /** Highlights the group when one of its children is the current page. */
  containsActive?: boolean;
  children: ReactNode;
}

/** Collapsible group of NavItems ("Applications & permits ▾"). Expanded nav only. */
export const CdNavGroup = ({
  label,
  icon: Icon,
  depth = 0,
  expanded,
  onToggle,
  containsActive = false,
  children,
}: CdNavGroupProps) => {
  const listId = useId();
  return (
    <>
      <button
        type="button"
        className={cx(
          styles.item,
          styles.group,
          styles[`depth${depth}`],
          containsActive && styles.containsActive,
        )}
        aria-expanded={expanded}
        aria-controls={listId}
        onClick={onToggle}
      >
        {Icon && <Icon className={styles.icon} aria-hidden="true" />}
        <span className={styles.label}>{label}</span>
        <ChevronDown
          className={cx(styles.chevron, expanded && styles.chevronOpen)}
          aria-hidden="true"
        />
      </button>
      <ul id={listId} className={styles.children} hidden={!expanded}>
        {children}
      </ul>
    </>
  );
};
